/**
 * @fileoverview Turning NIP-19/NIP-21 identifiers into routes in THIS app.
 *
 * `identifiers.ts` next door is a byte-identical lift from cloistr-space and is
 * deliberately left that way so it can be promoted into a shared package
 * without a diff. It carries `profilePath()` and `notePath()`, which hardcode
 * Space's `/p/` and `/e/` routes -- Pages has neither. So the route-shaped part
 * lives here instead of being edited into the lifted file.
 *
 * WHY STORED LINKS ARE `nostr:` URIs AND NOT OUR OWN HTTPS URLS. A page that
 * stores `https://pages.cloistr.xyz/naddr1...` has hardcoded our hostname into
 * content whose entire pitch is that it is portable: it breaks on a self-hosted
 * instance, in another client, and on any future domain of ours. The stored
 * form is `nostr:naddr1...` (NIP-21) and the https URL is only ever a
 * RENDERING, produced here, at display time.
 */

import { nip19 } from 'nostr-tools';

import { decodeIdentifier, type Identifier } from './identifiers';

/** The canonical in-app route for a page, given its addressable coordinate. */
export function pagePath(address: {
  kind: number;
  pubkey: string;
  identifier: string;
  relays?: string[];
}): string {
  return `/${encodeAddress(address)}`;
}

/** Where a page that does not exist yet is written. */
export const NEW_PAGE_PATH = '/write';

/**
 * The in-app route for editing an existing page.
 *
 * It holds the page's ADDRESS, the same durable coordinate the reading route
 * uses, so an edit URL is as bookmarkable as a page URL and survives every
 * revision. An event id here would go stale the first time the page was saved.
 */
export function editPath(address: {
  kind: number;
  pubkey: string;
  identifier: string;
  relays?: string[];
}): string {
  return `${NEW_PAGE_PATH}/${encodeAddress(address)}`;
}

/**
 * The in-app route for an author's index, given an `npub` or `nprofile`.
 *
 * One segment and no `/p/` prefix, for the same reason a page has none: the
 * identifier already says what it is, so the URL does not need to repeat it.
 * `identifiers.ts` carries a `profilePath()` that hardcodes cloistr-space's
 * `/p/` route; it is left untouched there so that file stays a clean lift.
 */
export function authorPath(identifier: string): string {
  return `/${identifier}`;
}

/**
 * Pack an addressable coordinate into an `naddr`.
 *
 * Missing from the lifted module, which can decode an naddr but not produce
 * one. Relay hints are included when we have them, for the same reason
 * `encodeEvent` includes them: an identifier that names no relay resolves only
 * for a reader already connected to the right one.
 */
export function encodeAddress(address: {
  kind: number;
  pubkey: string;
  identifier: string;
  relays?: string[];
}): string {
  return nip19.naddrEncode({
    kind: address.kind,
    pubkey: address.pubkey,
    identifier: address.identifier,
    relays: (address.relays ?? []).filter((r) => r.startsWith('ws')).slice(0, 3),
  });
}

/**
 * Resolve a `nostr:` URI found in page content to a local route, or null.
 *
 * Null means "we cannot render this as a link here" and the caller leaves the
 * raw URI visible. That is the graceful-degradation contract: a client that
 * does not understand a reference shows it unhelpfully rather than swallowing
 * it, so the reader can still copy it somewhere that does.
 *
 * NEVER THROWS. Page content is written by other people and reaches us from a
 * relay. `decodeIdentifier` throws on an nsec -- correct behaviour for a paste
 * box, wrong for a renderer, where one hostile or careless link must not blank
 * the whole page. An nsec in someone's markdown is caught here and refused as
 * unlinkable.
 */
export function localRouteForNostrUri(
  uri: string,
  pageKinds: readonly number[]
): string | null {
  let decoded: Identifier | null;
  try {
    decoded = decodeIdentifier(uri);
  } catch {
    // Includes SecretKeyPastedError. Somebody pasted a private key into their
    // own page; we render it as inert text and do not make it clickable.
    return null;
  }
  if (!decoded) return null;

  // Only addresses of a kind we render are pages, and there are two: our own,
  // which is every page we publish, and 30023, which we READ and never write --
  // a stranger's article, or an author's own written in another client. An
  // `naddr` for a wiki article or a calendar event is a real reference to
  // something we cannot display, and linking it to a route that will say "not
  // available" would be a worse answer than leaving it alone.
  if (decoded.type !== 'address' || !pageKinds.includes(decoded.kind)) return null;

  return pagePath(decoded);
}
